import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Header } from '@/components/header';
import { Footer } from '@/components/footer';
import { Partners } from '@/components/partners';
import Cta from '@/components/cta';
import { FAQ } from '../faq';

interface ProblemItem {
  title: string;
  description: string;
}

interface CaseStudyTemplateProps {
  id: string;
  title: string;
  description: string;
  video: string;
  image: string;
  problem?: string; // For backward compatibility
  solution: string;
  results: string;
  features: Array<{
    title: string;
    description: string;
    icon: any;
  }>;
  faqs?: Array<{ // Made optional with ?
    id?: string;
    question: string;
    answer: string;
  }>;
  testimonial?: {
    text: string;
    author: string;
    role: string;
    company: string;
  };
}

export const CaseStudyTemplate: React.FC<CaseStudyTemplateProps> = ({
  id,
  title,
  description,
  image,
  video,
  problem: propProblem,
  solution,
  results,
  features,
  faqs,
}) => {
  const { t } = useTranslation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-slate-900">
      <Header />

      {/* Hero Section with Full-screen Video/Image Background */}
      <section className="relative h-screen w-full overflow-hidden">
        {/* Background with Overlay */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-black/40" />
          {video ? (
            <video
              autoPlay
              loop
              muted
              playsInline
              className="h-full w-full object-cover"
            >
              <source src={video} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          ) : (
            <img
              src={image}
              alt={title}
              className="h-full w-full object-cover"
            />
          )}
        </div>

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col justify-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight tracking-tight">
              {t(`caseStudies.${id}.title`, title)}
            </h1> 
            <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto">
              {t(`caseStudies.${id}.description`, description)}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
              <a 
                href="#contact" 
                className="px-8 py-3 bg-white text-slate-900 font-bold rounded-full hover:bg-gray-100 transition-colors duration-300 text-lg"
              >
                {t('cta.primaryButton', 'Request a Demo')}
              </a>
              <a 
                href="#solutions" 
                className="px-8 py-3 border-2 border-white text-white font-bold rounded-full hover:bg-white/10 transition-colors duration-300 text-lg"
              >
                {t('cta.secondaryButton', 'Contact Us')}
              </a>
            </div>
            
            {/* Scroll Indicator */}
            <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 opacity-40">
              <button 
                onClick={() => {
                  window.scrollTo({
                    top: window.innerHeight,
                    behavior: 'smooth'
                  });
                }}
                aria-label="Scroll down"
                className="flex flex-col items-center text-white hover:text-primary-200 transition-colors group"
              >
                <span className="text-sm font-medium mb-2 opacity-80 group-hover:opacity-100 transition-opacity">
                  Scroll down
                </span>
                <div className="w-10 h-16 rounded-full border-2 border-white/50 flex items-start justify-center p-1 group-hover:border-white transition-colors">
                  <div className="w-1 h-4 bg-white rounded-full animate-bounce" />
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-8 md:p-12 shadow-lg border border-slate-200 dark:border-slate-700">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-8 text-slate-900 dark:text-white">
              {t(`caseStudies.problem`, 'The Problem')}
            </h2>
            <div className="space-y-6">
              {t(`caseStudies.${id}.problems`, { returnObjects: true, defaultValue: null }) ? (
                (t(`caseStudies.${id}.problems`, { returnObjects: true }) as ProblemItem[]).map((problem: ProblemItem, index: number) => (
                  <div key={index} className="bg-white dark:bg-slate-700 p-6 rounded-lg shadow">
                    <h3 className="text-xl font-semibold mb-2 text-slate-800 dark:text-white">{problem.title}</h3>
                    <p className="text-slate-600 dark:text-slate-300">{problem.description}</p>
                  </div>
                ))
              ) : (
                <div className="prose prose-lg prose-slate dark:prose-invert max-w-none">
                  <p className="text-slate-700 dark:text-slate-300">
                    {t(`caseStudies.${id}.problem`, { defaultValue: propProblem || '' })}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-8 md:p-12 shadow-lg border border-slate-200 dark:border-slate-700">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-8 text-slate-900 dark:text-white">
              {t(`caseStudies.solution`, 'Our Solution')}
            </h2>
            <div className="prose prose-lg prose-slate dark:prose-invert max-w-none mb-12">
              <p className="text-slate-700 dark:text-slate-300">{t(`caseStudies.${id}.solution`, solution)}</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-800 dark:text-slate-200">
                      <FontAwesomeIcon icon={feature.icon} className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-slate-900 dark:text-white">
                        {t(`caseStudies.${id}.features.${index}.title`, feature.title)}
                      </h3>
                      <p className="text-slate-600 dark:text-slate-400">
                        {t(`caseStudies.${id}.features.${index}.description`, feature.description)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-lg border border-slate-200 dark:border-slate-700">
                <h3 className="font-bold text-xl text-slate-900 dark:text-white mb-6">
                  {t('caseStudies.keyFeatures', 'Key Features')}
                </h3>
                <ul className="space-y-4">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <svg className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-slate-700 dark:text-slate-300">
                        {t(`caseStudies.${id}.features.${index}.title`, feature.title)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-8 md:p-12 shadow-lg border border-slate-200 dark:border-slate-700">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-8 text-slate-900 dark:text-white">
              {t(`caseStudies.results`, 'The Results')}
            </h2>
            <div className="space-y-6">
              {t(`caseStudies.${id}.results`, { returnObjects: true, defaultValue: null }) ? (
                (t(`caseStudies.${id}.results`, { returnObjects: true }) as ProblemItem[]).map((result: ProblemItem, index: number) => (
                  <div key={index} className="bg-white dark:bg-slate-700 p-6 rounded-lg shadow">
                    <h3 className="text-xl font-semibold mb-2 text-slate-800 dark:text-white">{result.title}</h3>
                    <p className="text-slate-600 dark:text-slate-300">{result.description}</p>
                  </div>
                ))
              ) : (
                <div className="prose prose-lg prose-slate dark:prose-invert max-w-none">
                  <p className="text-slate-700 dark:text-slate-300">
                    {t(`caseStudies.${id}.results`, { defaultValue: results || '' })}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {faqs && faqs.length > 0 && (
        <FAQ 
          faqs={faqs}
          title={t('faq.title', 'Frequently Asked Questions')}
          subtitle={t(`caseStudies.${id}.faqSubtitle`, 'Everything you need to know about the case study')}
        />
      )}
      <Partners />
      <Cta />
      <Footer />
    </div>
  );
};

export default CaseStudyTemplate;
